import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Space,
  message,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Switch,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DownloadOutlined,
  ReloadOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

const FiscalYearReports = () => {
  const [fiscalYears, setFiscalYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingFy, setEditingFy] = useState(null);
  const [form] = Form.useForm();

  const isEditing = !!editingFy;

  const fetchFiscalYears = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/fiscal-years');
      setFiscalYears(res.data.data || []);
    } catch (err) {
      message.error('Failed to fetch fiscal years');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiscalYears();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadReport = async (record) => {
    setDownloading(record.id);
    try {
      const response = await api.get(`/admin/fiscal-years/${record.id}/report`, {
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `fiscal_year_report_${record.label}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      message.success(`Report for ${record.label} downloaded`);
    } catch (err) {
      message.error('Failed to download report');
    } finally {
      setDownloading(null);
    }
  };

  const handleSubmit = async (values) => {
    const payload = {
      label: values.label,
      start_date: values.start_date.format('YYYY-MM-DD'),
      end_date: values.end_date.format('YYYY-MM-DD'),
      status: values.status || 'open',
      is_current: !!values.is_current,
      notes: values.notes || null,
    };

    try {
      if (isEditing) {
        await api.put(`/admin/fiscal-years/${editingFy.id}`, payload);
        message.success('Fiscal year updated');
      } else {
        await api.post('/admin/fiscal-years', payload);
        message.success('Fiscal year created');
      }
      setModalVisible(false);
      setEditingFy(null);
      form.resetFields();
      fetchFiscalYears();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to save fiscal year');
    }
  };

  const openCreate = () => {
    setEditingFy(null);
    form.resetFields();
    form.setFieldsValue({ status: 'open', is_current: false });
    setModalVisible(true);
  };

  const openEdit = (record) => {
    setEditingFy(record);
    form.setFieldsValue({
      label: record.label,
      start_date: dayjs(record.start_date),
      end_date: dayjs(record.end_date),
      status: record.status,
      is_current: !!record.is_current,
      notes: record.notes || '',
    });
    setModalVisible(true);
  };

  const columns = [
    {
      title: 'Fiscal Year',
      dataIndex: 'label',
      key: 'label',
      render: (label, record) => (
        <Space>
          <strong>{label}</strong>
          {record.is_current && <Tag color="green">Current</Tag>}
        </Space>
      ),
    },
    {
      title: 'Date Range',
      key: 'range',
      render: (_, record) => (
        <Text>
          {dayjs(record.start_date).format('DD MMM YYYY')} →{' '}
          {dayjs(record.end_date).format('DD MMM YYYY')}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'closed' ? 'red' : 'blue'}>
          {status === 'closed' ? 'Closed' : 'Open'}
        </Tag>
      ),
    },
    {
      title: 'Orders',
      key: 'orders',
      render: (_, record) => record.stats?.orders ?? 0,
    },
    {
      title: 'Revenue (NPR)',
      key: 'revenue',
      render: (_, record) => (record.stats?.revenue ?? 0).toLocaleString(undefined, {
        maximumFractionDigits: 2,
      }),
    },
    {
      title: 'Purchases (NPR)',
      key: 'purchases',
      render: (_, record) => (record.stats?.purchases ?? 0).toLocaleString(undefined, {
        maximumFractionDigits: 2,
      }),
    },
    {
      title: 'Invoices',
      key: 'invoices',
      render: (_, record) => record.stats?.invoices ?? 0,
    },
    {
      title: 'VAT (NPR)',
      key: 'vat',
      render: (_, record) => (record.stats?.vat_collected ?? 0).toLocaleString(undefined, {
        maximumFractionDigits: 2,
      }),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title={`Download full report for FY ${record.label}`}>
            <Button
              type="primary"
              size="small"
              icon={<DownloadOutlined />}
              loading={downloading === record.id}
              onClick={() => downloadReport(record)}
            >
              Report
            </Button>
          </Tooltip>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
            Edit
          </Button>
        </Space>
      ),
    },
  ];

  const current = fiscalYears.find((fy) => fy.is_current);

  return (
    <div>
      <Card>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={6}>
            <Statistic
              title="Current Fiscal Year"
              value={current?.label || '—'}
              prefix={<CalendarOutlined />}
            />
          </Col>
          <Col xs={24} sm={6}>
            <Statistic
              title="Current FY Orders"
              value={current?.stats?.orders ?? 0}
              prefix={<CheckCircleOutlined />}
            />
          </Col>
          <Col xs={24} sm={6}>
            <Statistic
              title="Current FY Revenue (NPR)"
              value={current?.stats?.revenue ?? 0}
              precision={2}
              prefix={<CheckCircleOutlined />}
            />
          </Col>
          <Col xs={24} sm={6} style={{ textAlign: 'right' }}>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={fetchFiscalYears}>
                Refresh
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                New Fiscal Year
              </Button>
            </Space>
          </Col>
        </Row>

        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Fiscal years run Shrawan 1 to Asar 31 (e.g. FY 2083/84 = 2026-07-17 → 2027-07-16). The
          current fiscal year is detected automatically. Reports include Summary, itemized Sales,
          itemized Purchases and Stock movement (opening/closing stock, with stock carrying forward
          into the next fiscal year).
        </Text>

        <Table
          columns={columns}
          dataSource={fiscalYears}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20, showTotal: (total) => `${total} fiscal years` }}
          scroll={{ x: 1100 }}
        />
      </Card>

      <Modal
        title={isEditing ? `Edit Fiscal Year: ${editingFy.label}` : 'New Fiscal Year'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingFy(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="label"
                label="Label"
                rules={[{ required: true, message: 'Enter fiscal year label (e.g. 2083/84)' }]}
              >
                <Input placeholder="e.g. 2083/84" maxLength={20} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="status" label="Status" rules={[{ required: true }]}>
                <Select
                  options={[
                    { value: 'open', label: 'Open' },
                    { value: 'closed', label: 'Closed' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="start_date"
                label="Start Date"
                rules={[{ required: true, message: 'Select start date' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="end_date"
                label="End Date"
                rules={[{ required: true, message: 'Select end date' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label="Notes">
            <TextArea rows={2} placeholder="Optional notes" maxLength={1000} />
          </Form.Item>

          <Form.Item name="is_current" label="Set as current fiscal year" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Button type="primary" htmlType="submit" block size="large">
            {isEditing ? 'Update Fiscal Year' : 'Create Fiscal Year'}
          </Button>
        </Form>
      </Modal>
    </div>
  );
};

export default FiscalYearReports;
